import { useState, useMemo, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { AttendanceRecord } from '@/mocks/attendance';
import { classSections, classTeacherMap } from '@/mocks/students';
import type { IncidentLog } from '@/mocks/preInspection';
import { ConfirmDialog } from '@/components/base/Modal';
import CallDropdown from '@/components/feature/CallParentDropdown';
import ActionDropdown from '@/pages/dashboard/admin/attendance/components/ActionDropdown';
import IncidentTooltip from '@/components/feature/IncidentTooltip';
import { students } from '@/mocks/students';

interface AttendanceTableProps {
  records: AttendanceRecord[];
  selectedDate: string;
  groupedByClass: boolean;
  onToggle: (recordId: string) => void;
  onRemarkChange: (recordId: string, remark: string) => void;
  onMarkAll: (status: 'present' | 'absent') => void;
  searchQuery: string;
  classFilter: string;
  onPreInspection: (studentId: string, studentName: string, className: string) => void;
  onIncident: (studentId: string, studentName: string, className: string) => void;
  incidentLogs: IncidentLog[];
}

export default function AttendanceTable({
  records,
  selectedDate,
  groupedByClass,
  onToggle,
  onRemarkChange,
  onMarkAll,
  searchQuery,
  classFilter,
  onPreInspection,
  onIncident,
  incidentLogs,
}: AttendanceTableProps) {
  const [editingRemarkId, setEditingRemarkId] = useState<string | null>(null);
  const [remarkDraft, setRemarkDraft] = useState('');
  const [confirmAction, setConfirmAction] = useState<'present' | 'absent' | null>(null);
  const [hoveredIncidentStudentId, setHoveredIncidentStudentId] = useState<string | null>(null);
  const [tooltipRect, setTooltipRect] = useState<DOMRect | null>(null);
  const incidentBtnRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const { t } = useTranslation();

  const setIncidentBtnRef = useCallback((studentId: string) => (el: HTMLButtonElement | null) => {
    if (el) {
      incidentBtnRefs.current.set(studentId, el);
    } else {
      incidentBtnRefs.current.delete(studentId);
    }
  }, []);

  const showIncidentTooltip = useCallback((studentId: string) => {
    const btn = incidentBtnRefs.current.get(studentId);
    if (btn) {
      setTooltipRect(btn.getBoundingClientRect());
    }
    setHoveredIncidentStudentId(studentId);
  }, []);

  const hideIncidentTooltip = useCallback(() => {
    setHoveredIncidentStudentId(null);
    setTooltipRect(null);
  }, []);

  // Build incident lookup: studentId → incidents on selectedDate
  const incidentsByStudentOnDate: Record<string, typeof incidentLogs> = {};
  incidentLogs.forEach((inc) => {
    if (inc.date !== selectedDate) return;
    if (!incidentsByStudentOnDate[inc.studentId]) {
      incidentsByStudentOnDate[inc.studentId] = [];
    }
    incidentsByStudentOnDate[inc.studentId].push(inc);
  });

  const todayStr = new Date().toISOString().split('T')[0];
  const isEditable = selectedDate === todayStr;

  const studentContactMap = useMemo(() => {
    const map: Record<string, { contactF: string; contactM: string; fatherName: string; motherName: string }> = {};
    for (const s of students) {
      map[s.studentId] = {
        contactF: s.contactNumberF,
        contactM: s.contactNumberM,
        fatherName: s.fatherName,
        motherName: s.motherName,
      };
    }
    return map;
  }, []);

  const filtered = useMemo(() => {
    let result = [...records];
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (r) =>
          r.studentName.toLowerCase().includes(q) ||
          r.studentId.toLowerCase().includes(q),
      );
    }
    if (classFilter) {
      result = result.filter((r) => r.className === classFilter);
    }
    return result;
  }, [records, searchQuery, classFilter]);

  const presentCount = filtered.filter((r) => r.status === 'present').length;
  const absentCount = filtered.filter((r) => r.status === 'absent').length;

  const grouped = useMemo(() => {
    if (!groupedByClass) return null;
    const map: Record<string, AttendanceRecord[]> = {};
    for (const record of filtered) {
      if (!map[record.className]) map[record.className] = [];
      map[record.className].push(record);
    }
    const ordered: { className: string; records: AttendanceRecord[]; teacher: string }[] = [];
    for (const cls of classSections) {
      if (map[cls]) {
        ordered.push({ className: cls, records: map[cls], teacher: classTeacherMap[cls] || '' });
      }
    }
    return ordered;
  }, [filtered, groupedByClass]);

  const startEditingRemark = (record: AttendanceRecord) => {
    setEditingRemarkId(record.id);
    setRemarkDraft(record.remark);
  };

  const saveRemark = (recordId: string) => {
    onRemarkChange(recordId, remarkDraft);
    setEditingRemarkId(null);
    setRemarkDraft('');
  };

  const cancelRemark = () => {
    setEditingRemarkId(null);
    setRemarkDraft('');
  };

  const renderRow = (record: AttendanceRecord) => (
    <tr key={record.id} className="hover:bg-background-50/60 transition-colors">
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 flex items-center justify-center rounded-full bg-accent-100 text-accent-700 text-xs font-heading font-700 flex-shrink-0">
            {record.studentName.split(' ').map((n) => n[0]).join('')}
          </div>
          <div>
            <p className="text-sm font-medium text-foreground-800 font-label">{record.studentName}</p>
            <p className="text-xs text-foreground-400 font-mono">{record.studentId}</p>
          </div>
        </div>
      </td>
      {groupedByClass ? null : (
        <td className="px-4 py-3">
          <span className="text-xs bg-accent-50 text-accent-800 border border-accent-100 px-2 py-1 rounded-full font-label whitespace-nowrap">
            {record.className}
          </span>
        </td>
      )}
      <td className="px-4 py-3">
        {isEditable ? (
          <button
            onClick={() => onToggle(record.id)}
            className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors duration-200 cursor-pointer whitespace-nowrap ${
              record.status === 'present'
                ? 'bg-semantic-success'
                : 'bg-semantic-error'
            }`}
            title={record.status === 'present' ? t('attendance.presentClickToAbsent') : t('attendance.absentClickToPresent')}
          >
            <span
              className={`inline-block h-5 w-5 transform rounded-full bg-background-50 transition-transform duration-200 ${
                record.status === 'present' ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        ) : (
          <span
            className={`inline-flex items-center gap-1.5 text-xs font-label font-medium px-2.5 py-1 rounded-full ${
              record.status === 'present'
                ? 'bg-semantic-success-bg text-foreground-800 border border-semantic-success/30'
                : 'bg-semantic-error-bg text-foreground-800 border border-semantic-error/30'
            }`}
          >
            <span className={`w-1.5 h-1.5 rounded-full ${record.status === 'present' ? 'bg-semantic-success' : 'bg-semantic-error'}`} />
            {record.status === 'present' ? 'Present' : 'Absent'}
          </span>
        )}
      </td>
      <td className="px-4 py-3 max-w-[220px]">
        {editingRemarkId === record.id ? (
          <div className="flex items-center gap-1">
            <input
              type="text"
              value={remarkDraft}
              onChange={(e) => setRemarkDraft(e.target.value)}
              placeholder={t('attendance.addRemark')}
              className="flex-1 px-2 py-1 text-xs font-label rounded-md border border-secondary-300 bg-background-50 text-foreground-800 focus:outline-none focus:ring-2 focus:ring-secondary-300 focus:border-secondary-400"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') saveRemark(record.id);
                if (e.key === 'Escape') cancelRemark();
              }}
            />
            <button
              onClick={() => saveRemark(record.id)}
              className="w-6 h-6 flex items-center justify-center rounded-md bg-semantic-success text-background-50 cursor-pointer whitespace-nowrap"
              title="Save"
            >
              <i className="ri-check-line text-xs" />
            </button>
            <button
              onClick={cancelRemark}
              className="w-6 h-6 flex items-center justify-center rounded-md bg-background-100 text-foreground-500 hover:text-foreground-700 cursor-pointer whitespace-nowrap"
              title="Cancel"
            >
              <i className="ri-close-line text-xs" />
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-1.5 group">
            {record.remark ? (
              <span className="text-xs text-foreground-600 font-label truncate block">{record.remark}</span>
            ) : (
              <span className="text-xs text-foreground-300 font-label italic">{t('attendance.noRemark')}</span>
            )}
            {isEditable && (
              <button
                onClick={() => startEditingRemark(record)}
                className="w-5 h-5 flex items-center justify-center rounded text-foreground-400 hover:text-accent-600 hover:bg-accent-50 cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                title={t('attendance.editRemark')}
              >
                <i className="ri-edit-line text-xs" />
              </button>
            )}
          </div>
        )}
      </td>
      <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-center relative">
          {(() => {
            const studentIncidents = incidentsByStudentOnDate[record.studentId];
            if (!studentIncidents || studentIncidents.length === 0) {
              return (
                <div className="w-8 h-8 flex items-center justify-center">
                  <div className="w-1.5 h-1.5 rounded-full bg-foreground-200" />
                </div>
              );
            }
            return (
              <>
                <button
                  ref={setIncidentBtnRef(record.studentId)}
                  type="button"
                  className="w-8 h-8 flex items-center justify-center rounded-full bg-red-50 hover:bg-red-100 transition-colors cursor-pointer relative"
                  onMouseEnter={() => showIncidentTooltip(record.studentId)}
                  onMouseLeave={hideIncidentTooltip}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (hoveredIncidentStudentId === record.studentId) {
                      hideIncidentTooltip();
                    } else {
                      showIncidentTooltip(record.studentId);
                    }
                  }}
                >
                  <i className="ri-alert-fill text-red-500 text-base" />
                  {studentIncidents.length > 1 && (
                    <span className="absolute -top-1 -right-1 w-4 h-4 flex items-center justify-center rounded-full bg-red-600 text-white text-[9px] font-label font-bold">
                      {studentIncidents.length}
                    </span>
                  )}
                </button>
              </>
            );
          })()}
        </div>
      </td>
      <td className="px-3 py-3">
        <div className="flex justify-center">
          <ActionDropdown
            onPreInspection={() => onPreInspection(record.studentId, record.studentName, record.className)}
            onIncident={() => onIncident(record.studentId, record.studentName, record.className)}
            studentName={record.studentName}
          />
        </div>
      </td>
      <td className="px-3 py-3">
        <div className="flex justify-center">
          {(() => {
            const contact = studentContactMap[record.studentId];
            if (contact) {
              return <CallDropdown contactF={contact.contactF} contactM={contact.contactM} fatherName={contact.fatherName} motherName={contact.motherName} />;
            }
            return <span className="text-xs text-foreground-300 italic">—</span>;
          })()}
        </div>
      </td>
    </tr>
  );

  // Mobile card rendering — thumb-friendly layout for phones
  const renderMobileCard = (record: AttendanceRecord) => {
    const contact = studentContactMap[record.studentId];
    const initials = record.studentName.split(' ').map((n) => n[0]).join('');
    return (
      <div
        key={record.id}
        className={`p-3 rounded-xl border ${
          record.status === 'present'
            ? 'border-semantic-success/20 bg-semantic-success-bg/30'
            : 'border-semantic-error/20 bg-semantic-error-bg/30'
        }`}
      >
        {/* Top row: avatar + name + call */}
        <div className="flex items-center gap-2.5 mb-2">
          <div className="w-9 h-9 flex items-center justify-center rounded-full bg-accent-100 text-accent-700 text-xs font-heading font-700 flex-shrink-0">
            {initials}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-label font-medium text-foreground-800 truncate">{record.studentName}</p>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="text-xs text-foreground-400 font-mono">{record.studentId}</span>
              {!groupedByClass && (
                <span className="text-[10px] bg-accent-100 text-accent-700 px-1.5 py-0.5 rounded-full font-label whitespace-nowrap">
                  {record.className}
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1">
            <div className="relative">
              {(() => {
                const studentIncidents = incidentsByStudentOnDate[record.studentId];
                if (studentIncidents && studentIncidents.length > 0) {
                  return (
                    <>
                      <button
                        ref={setIncidentBtnRef(record.studentId)}
                        type="button"
                        className="w-8 h-8 flex items-center justify-center rounded-full bg-red-50 hover:bg-red-100 transition-colors cursor-pointer relative"
                        onMouseEnter={() => showIncidentTooltip(record.studentId)}
                        onMouseLeave={hideIncidentTooltip}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (hoveredIncidentStudentId === record.studentId) {
                            hideIncidentTooltip();
                          } else {
                            showIncidentTooltip(record.studentId);
                          }
                        }}
                      >
                        <i className="ri-alert-fill text-red-500 text-base" />
                        {studentIncidents.length > 1 && (
                          <span className="absolute -top-1 -right-1 w-4 h-4 flex items-center justify-center rounded-full bg-red-600 text-white text-[9px] font-label font-bold">
                            {studentIncidents.length}
                          </span>
                        )}
                      </button>
                    </>
                  );
                }
                return null;
              })()}
            </div>
            <ActionDropdown
              onPreInspection={() => onPreInspection(record.studentId, record.studentName, record.className)}
              onIncident={() => onIncident(record.studentId, record.studentName, record.className)}
              studentName={record.studentName}
            />
            {contact && (
              <CallDropdown contactF={contact.contactF} contactM={contact.contactM} fatherName={contact.fatherName} motherName={contact.motherName} />
            )}
          </div>
        </div>

        {/* Middle: full-width attendance toggle */}
        <div className="mb-2">
          {isEditable ? (
            <button
              onClick={() => onToggle(record.id)}
              className={`w-full flex items-center justify-center gap-2 h-10 rounded-lg text-sm font-label font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap ${
                record.status === 'present'
                  ? 'bg-semantic-success text-background-50 shadow-[0_0_0_2px_rgba(34,197,94,0.3)]'
                  : 'bg-semantic-error text-background-50 shadow-[0_0_0_2px_rgba(239,68,68,0.3)]'
              }`}
            >
              <i className={`text-base ${record.status === 'present' ? 'ri-check-line' : 'ri-close-line'}`} />
              {record.status === 'present' ? t('attendance.presentLabel') : t('attendance.absentLabel')}
              <span className="text-xs opacity-80 font-normal">{t('attendance.tapToToggle')}</span>
            </button>
          ) : (
            <span
              className={`w-full flex items-center justify-center gap-2 h-10 rounded-lg text-sm font-label font-semibold ${
                record.status === 'present'
                  ? 'bg-semantic-success-bg text-foreground-800 border border-semantic-success/30'
                  : 'bg-semantic-error-bg text-foreground-800 border border-semantic-error/30'
              }`}
            >
              <span className={`w-2 h-2 rounded-full ${record.status === 'present' ? 'bg-semantic-success' : 'bg-semantic-error'}`} />
              {record.status === 'present' ? t('attendance.presentLabel') : t('attendance.absentLabel')}
            </span>
          )}
        </div>

        {/* Remark on its own line */}
        <div>
          {editingRemarkId === record.id ? (
            <div className="flex items-center gap-1">
              <input
                type="text"
                value={remarkDraft}
                onChange={(e) => setRemarkDraft(e.target.value)}
                placeholder="Remark..."
                className="flex-1 px-2 py-1.5 text-xs font-label rounded-md border border-secondary-300 bg-background-50 text-foreground-800 focus:outline-none focus:ring-2 focus:ring-secondary-300 focus:border-secondary-400"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') saveRemark(record.id);
                  if (e.key === 'Escape') cancelRemark();
                }}
              />
              <button
                onClick={() => saveRemark(record.id)}
                className="w-7 h-7 flex items-center justify-center rounded-md bg-semantic-success text-background-50 cursor-pointer flex-shrink-0 whitespace-nowrap"
              >
                <i className="ri-check-line text-xs" />
              </button>
            </div>
          ) : (
            <button
              onClick={() => isEditable && startEditingRemark(record)}
              className={`w-full text-left px-2 py-1.5 rounded-md text-xs font-label transition-colors ${
                record.remark
                  ? 'text-foreground-600 bg-background-100/70'
                  : 'text-foreground-300 italic'
              } ${isEditable ? 'cursor-pointer hover:bg-background-200/70' : 'cursor-default'}`}
            >
              {record.remark || t('attendance.noRemark')}
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="bg-background-50 rounded-xl border border-background-200 overflow-hidden">
      {/* Summary + Bulk Actions */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between px-5 py-3 border-b border-background-200 gap-3">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-semantic-success" />
            <span className="text-sm font-label text-foreground-700">
              <span className="font-700">{presentCount}</span> {t('attendance.presentLabel')}
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-semantic-error" />
            <span className="text-sm font-label text-foreground-700">
              <span className="font-700">{absentCount}</span> {t('attendance.absentLabel')}
            </span>
          </div>
        </div>
        {isEditable && filtered.length > 0 && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => setConfirmAction('present')}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-label font-medium rounded-lg bg-semantic-success-bg text-foreground-800 border border-semantic-success/30 hover:bg-semantic-success/20 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-check-double-line text-sm" />
              {t('attendance.markAllPresent')}
            </button>
            <button
              onClick={() => setConfirmAction('absent')}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-label font-medium rounded-lg bg-semantic-error-bg text-foreground-800 border border-semantic-error/30 hover:bg-semantic-error/20 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-forbid-line text-sm" />
              {t('attendance.markAllAbsent')}
            </button>
          </div>
        )}
      </div>

      {/* Desktop Table */}
      <div className="hidden sm:block overflow-x-auto">
        <table className="w-full" role="table">
          <thead>
            <tr className="border-b border-background-100">
              <th scope="col" className="text-left px-4 py-3 text-xs font-label font-600 text-foreground-500 uppercase tracking-wide">{t('attendance.student')}</th>
              {!groupedByClass && (
                <th scope="col" className="text-left px-4 py-3 text-xs font-label font-600 text-foreground-500 uppercase tracking-wide">{t('attendance.class')}</th>
              )}
              <th scope="col" className="text-left px-4 py-3 text-xs font-label font-600 text-foreground-500 uppercase tracking-wide">{t('attendance.attendance')}</th>
              <th scope="col" className="text-left px-4 py-3 text-xs font-label font-600 text-foreground-500 uppercase tracking-wide">{t('attendance.remark')}</th>
              <th scope="col" className="text-center px-3 py-3 text-xs font-label font-600 text-foreground-500 uppercase tracking-wide">{t('attendance.alert')}</th>
              <th scope="col" className="text-center px-3 py-3 text-xs font-label font-600 text-foreground-500 uppercase tracking-wide">{t('attendance.actions')}</th>
              <th scope="col" className="text-center px-3 py-3 text-xs font-label font-600 text-foreground-500 uppercase tracking-wide">{t('attendance.call')}</th>
            </tr>
          </thead>
          {grouped ? (
            grouped.map((group) => (
              <tbody key={group.className}>
                <tr className="bg-background-100/50">
                  <td colSpan={groupedByClass ? 6 : 7} className="px-4 py-2.5">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 flex items-center justify-center rounded-md bg-secondary-100">
                        <i className="ri-building-4-line text-secondary-700 text-xs" />
                      </div>
                      <span className="text-sm font-heading font-700 text-foreground-800">{group.className}</span>
                      <span className="text-xs text-foreground-400 font-label">— {group.teacher}</span>
                      <span className="text-xs text-foreground-400 font-label ml-auto">
                        {group.records.filter((r) => r.status === 'present').length}/{group.records.length} present
                      </span>
                    </div>
                  </td>
                </tr>
                {group.records.map(renderRow)}
              </tbody>
            ))
          ) : (
            <tbody className="divide-y divide-background-100">
              {filtered.map(renderRow)}
            </tbody>
          )}
        </table>
      </div>

      {/* Mobile Card View */}
      <div className="sm:hidden px-3 py-3 flex flex-col gap-2">
        {grouped ? (
          grouped.map((group) => (
            <div key={group.className}>
              {/* Group header */}
              <div className="flex items-center gap-2 px-1 py-2">
                <div className="w-6 h-6 flex items-center justify-center rounded-md bg-secondary-100">
                  <i className="ri-building-4-line text-secondary-700 text-xs" />
                </div>
                <span className="text-sm font-heading font-700 text-foreground-800">{group.className}</span>
                <span className="text-xs text-foreground-400 font-label">— {group.teacher}</span>
                <span className="text-xs text-foreground-400 font-label ml-auto">
                  {group.records.filter((r) => r.status === 'present').length}/{group.records.length}
                </span>
              </div>
              <div className="flex flex-col gap-2">
                {group.records.map(renderMobileCard)}
              </div>
            </div>
          ))
        ) : (
          filtered.map(renderMobileCard)
        )}
      </div>

      {filtered.length === 0 && (
        <div className="px-5 py-16 text-center">
          <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-2xl bg-background-100 mb-4">
            <i className="ri-search-line text-foreground-300 text-2xl" />
          </div>
          <p className="text-sm font-medium text-foreground-500 font-label">{t('attendance.noAttendanceRecords')}</p>
          <p className="text-xs text-foreground-400 font-label mt-1">{t('common.tryAdjustingFilters')}</p>
        </div>
      )}

      {/* Footer */}
      <div className="px-5 py-3 border-t border-background-200 flex items-center justify-between">
        <p className="text-xs text-foreground-400 font-label">{filtered.length} students shown</p>
        {!isEditable && (
          <p className="text-xs text-foreground-400 font-label italic flex items-center gap-1">
            <i className="ri-lock-line text-xs" />
            {t('attendance.readOnlyHistorical')}
          </p>
        )}
        {isEditable && (
          <p className="text-xs text-foreground-400 font-label italic flex items-center gap-1">
            <i className="ri-information-line text-xs" />
            {t('attendance.toggleToMark')}
          </p>
        )}
      </div>

      {/* Incident Tooltip Portal */}
      <IncidentTooltip
        show={hoveredIncidentStudentId !== null}
        incidents={hoveredIncidentStudentId ? (incidentsByStudentOnDate[hoveredIncidentStudentId] || []) : []}
        triggerRect={tooltipRect}
        onMouseEnter={() => {
          if (hoveredIncidentStudentId) setHoveredIncidentStudentId(hoveredIncidentStudentId);
        }}
        onMouseLeave={hideIncidentTooltip}
      />

      {/* Mark All Confirmation Dialog */}
      <ConfirmDialog
        open={confirmAction !== null}
        onClose={() => setConfirmAction(null)}
        onConfirm={() => {
          if (confirmAction) {
            onMarkAll(confirmAction);
            setConfirmAction(null);
          }
        }}
        title={confirmAction === 'present' ? t('attendance.markAllPresentTitle') : t('attendance.markAllAbsentTitle')}
        message={
          confirmAction === 'present'
            ? t('attendance.markAllPresentMessage', { count: filtered.length })
            : t('attendance.markAllAbsentMessage', { count: filtered.length })
        }
        confirmLabel={confirmAction === 'present' ? t('attendance.markAllPresent') : t('attendance.markAllAbsent')}
        cancelLabel={t('common.cancel')}
        variant={confirmAction === 'absent' ? 'destructive' : 'default'}
      />
    </div>
  );
}